import { useState, useEffect } from "react";
import { Phone, PhoneIncoming, PhoneOutgoing, Trash2, RotateCcw } from "lucide-react";
import { Card } from "@/components/common";
import { api } from "@/lib/api";

const TelefonzentraleModulPage = () => {
  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showTrash, setShowTrash] = useState(false);
  const [trash, setTrash] = useState([]);

  useEffect(() => {
    load();
    loadTrash();
    const interval = setInterval(() => { load(); loadTrash(); }, 15000);
    return () => clearInterval(interval);
  }, []);

  const load = async () => {
    try {
      const res = await api.get("/fritzbox/calls");
      setCalls(Array.isArray(res.data) ? res.data : []);
    } catch { /* ignore */ } finally {
      setLoading(false);
    }
  };

  const loadTrash = async () => {
    try {
      const res = await api.get("/fritzbox/calls/trash");
      setTrash(Array.isArray(res.data) ? res.data : []);
    } catch { /* ignore */ }
  };

  const toggleTrash = async () => {
    if (!showTrash) await loadTrash();
    setShowTrash(s => !s);
  };

  const restoreCall = async (id) => {
    await api.post(`/fritzbox/calls/${id}/restore`);
    await loadTrash();
    await load();
  };

  // Alle Anrufe derselben Nummer (unabhaengig von Reihenfolge) zu einer Zeile mit Zaehler zusammenfassen
  const byKunde = new Map();
  for (const c of calls) {
    // Gruppierung nach Kunde (falls erkannt), sonst nach Nummer - so werden mehrere Rufnummern
    // derselben Person zusammengefasst statt getrennt angezeigt.
    const key = c.customer_id || `nr:${c.number}`;
    const existing = byKunde.get(key);
    if (existing) {
      existing.count += 1;
      if (c.timestamp > existing.timestamp) {
        existing.timestamp = c.timestamp;
        existing.customer_name = c.customer_name;
        existing.projekt_kategorie = c.projekt_kategorie;
        existing.number = c.number;
      }
    } else {
      byKunde.set(key, { ...c, count: 1 });
    }
  }
  const grouped = Array.from(byKunde.values()).sort((a, b) => (b.timestamp > a.timestamp ? 1 : -1));

  return (
    <div data-testid="telefonzentrale-page">
      {(() => {
        const baldWegAnzahl = trash.filter(c => {
          const purgeAm = new Date(new Date(c.deleted_at).getTime() + 5 * 24 * 60 * 60 * 1000);
          return Math.ceil((purgeAm - new Date()) / (1000 * 60 * 60 * 24)) <= 1;
        }).length;
        return baldWegAnzahl > 0 ? (
          <div className="mb-4 p-3 rounded-sm bg-red-50 border border-red-200 text-sm text-red-800 flex items-center gap-2" data-testid="fritzbox-purge-warning">
            <Trash2 className="w-4 h-4 shrink-0" />
            Achtung: {baldWegAnzahl} {baldWegAnzahl === 1 ? "Anruf steht" : "Anrufe stehen"} im Papierkorb zur endgueltigen Loeschung an.
          </div>
        ) : null;
      })()}
      <div className="mb-6 lg:mb-8 flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-2">
            <Phone className="w-6 h-6 text-primary" />
            <h1 className="text-2xl lg:text-4xl font-bold">Telefonzentrale</h1>
          </div>
          <p className="text-muted-foreground mt-1 text-sm lg:text-base">
            {grouped.length} {grouped.length === 1 ? "Anrufer" : "Anrufer"} &middot; {calls.length} Anrufe insgesamt
          </p>
        </div>
        <button
          type="button"
          onClick={toggleTrash}
          className={`flex items-center gap-2 px-3 py-1.5 rounded-full border text-sm font-medium transition-colors ${showTrash ? "bg-primary text-primary-foreground border-primary" : "bg-card hover:bg-muted"}`}
          data-testid="btn-toggle-trash"
        >
          <Trash2 className="w-4 h-4" /> Papierkorb {trash.length > 0 && !showTrash ? `(${trash.length})` : ""}
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      ) : showTrash ? (
        trash.length === 0 ? (
          <Card className="p-8 lg:p-12 text-center">
            <Trash2 className="w-10 h-10 lg:w-12 lg:h-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-base lg:text-lg font-semibold">Papierkorb ist leer</h3>
          </Card>
        ) : (
          <Card className="p-4 lg:p-6 rounded-xl">
            <p className="text-xs text-muted-foreground mb-3">Geloeschte Anrufe werden nach 5 Tagen endgueltig entfernt.</p>
            <ul className="space-y-3">
              {trash.map((c) => {
                const Icon = c.direction === "eingehend" ? PhoneIncoming : PhoneOutgoing;
                const zeit = c.timestamp ? new Date(c.timestamp).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "";
                return (
                  <li key={c.id} className="flex items-center gap-3 border-b border-border/40 pb-3 last:border-0">
                    <Icon className="w-5 h-5 shrink-0 text-muted-foreground" />
                    <span className="shrink-0 px-2.5 py-1 rounded-full bg-muted text-muted-foreground text-xs font-mono font-semibold tabular-nums">
                      {zeit || "\u2014"}
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium truncate text-muted-foreground">{c.customer_name || "Unbekannt"}</p>
                      <p className="text-xs text-muted-foreground truncate">{c.number}</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => restoreCall(c.id)}
                      className="p-2 rounded-sm hover:bg-primary/10 hover:text-primary shrink-0"
                      title="Wiederherstellen"
                    >
                      <RotateCcw className="w-4 h-4" />
                    </button>
                  </li>
                );
              })}
            </ul>
          </Card>
        )
      ) : grouped.length === 0 ? (
        <Card className="p-8 lg:p-12 text-center">
          <Phone className="w-10 h-10 lg:w-12 lg:h-12 mx-auto text-muted-foreground mb-4" />
          <h3 className="text-base lg:text-lg font-semibold">Noch keine Anrufe erfasst</h3>
        </Card>
      ) : (
        <Card className="p-4 lg:p-6 rounded-xl">
          <ul className="space-y-3">
            {grouped.map((c) => {
              const Icon = c.direction === "eingehend" ? PhoneIncoming : PhoneOutgoing;
              const zeit = c.timestamp ? new Date(c.timestamp).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "";
              return (
                <li key={`${c.number}-${c.direction}`} className="flex items-center gap-3 border-b border-border/40 pb-3 last:border-0" data-testid={`telefonzentrale-call-${c.number}`}>
                  <Icon className={`w-5 h-5 shrink-0 ${c.direction === "eingehend" ? "text-emerald-600" : "text-blue-600"}`} />
                  <span className="shrink-0 px-2.5 py-1 rounded-full bg-primary/10 text-primary text-xs font-mono font-semibold tabular-nums">
                    {zeit || "\u2014"}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium truncate">
                      {c.customer_name || "Unbekannt"}
                      {c.projekt_kategorie && <span className="ml-2 text-xs font-normal text-primary">({c.projekt_kategorie})</span>}
                      {c.count > 1 && <span className="ml-2 text-xs font-semibold text-muted-foreground">({c.count}x)</span>}
                    </p>
                    <p className="text-xs text-muted-foreground truncate">{c.number}</p>
                  </div>
                  <button
                    type="button"
                    onClick={async () => { await api.post(`/fritzbox/calls/${c.id}/delete`); load(); }}
                    className="p-2 rounded-sm hover:bg-destructive/10 hover:text-destructive shrink-0"
                    title="Loeschen (5 Tage wiederherstellbar)"
                    data-testid={`btn-delete-call-${c.number}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </li>
              );
            })}
          </ul>
        </Card>
      )}
    </div>
  );
};

export { TelefonzentraleModulPage };
